import React, { useMemo, useState } from "react";
import { Building2, Users } from "lucide-react";

/**
 * Interactive layered DAG visualization of the ownership graph.
 * - Layers computed via topological longest-path (backend guarantees acyclicity).
 * - Hovering a node highlights its incident edges and neighbours.
 */
export function OwnershipGraph({ nodes, edges, nodeMap }) {
  const [hover, setHover] = useState(null);

  const layout = useMemo(() => {
    const NODEW = 158, NODEH = 56, COLW = 230, ROWH = 96, PADX = 28, PADY = 28;
    const ids = nodes.map((n) => n.id);
    const idset = new Set(ids);
    const children = {}, indeg = {};
    ids.forEach((id) => { indeg[id] = 0; });
    edges.forEach((e) => {
      if (!idset.has(e.owner_id) || !idset.has(e.owned_id)) return;
      (children[e.owner_id] = children[e.owner_id] || []).push(e.owned_id);
      indeg[e.owned_id] = (indeg[e.owned_id] || 0) + 1;
    });
    const level = {}; ids.forEach((id) => { level[id] = 0; });
    const indeg2 = { ...indeg };
    const q = ids.filter((id) => indeg2[id] === 0);
    let guard = 0;
    while (q.length && guard < 5000) {
      const u = q.shift(); guard++;
      (children[u] || []).forEach((v) => {
        level[v] = Math.max(level[v], level[u] + 1);
        if (--indeg2[v] === 0) q.push(v);
      });
    }
    const byLevel = {};
    ids.forEach((id) => { (byLevel[level[id]] = byLevel[level[id]] || []).push(id); });
    const pos = {};
    Object.entries(byLevel).forEach(([lvl, group]) => {
      group.forEach((id, i) => { pos[id] = { x: PADX + Number(lvl) * COLW, y: PADY + i * ROWH }; });
    });
    const levels = Object.keys(byLevel).map(Number);
    const maxLevel = levels.length ? Math.max(...levels) : 0;
    const maxRows = Object.values(byLevel).reduce((m, a) => Math.max(m, a.length), 1);
    const width = PADX * 2 + maxLevel * COLW + NODEW;
    const height = PADY * 2 + (maxRows - 1) * ROWH + NODEH;
    return { pos, width, height, NODEW, NODEH };
  }, [nodes, edges]);

  const { pos, width, height, NODEW, NODEH } = layout;

  const isEdgeLit = (e) => hover && (e.owner_id === hover || e.owned_id === hover);
  const isNodeLit = (id) =>
    !hover ? false : hover === id || edges.some((e) => (e.owner_id === hover && e.owned_id === id) || (e.owned_id === hover && e.owner_id === id));

  return (
    <div className="overflow-auto rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-bg-2)] p-2" data-testid="ownership-graph-svg">
      <svg width={Math.max(width, 320)} height={Math.max(height, 200)} style={{ minWidth: "100%" }}>
        <defs>
          <marker id="arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L7,3 L0,6 Z" fill="rgba(176,141,87,0.85)" />
          </marker>
        </defs>

        {/* edges */}
        {edges.map((e) => {
          const a = pos[e.owner_id], b = pos[e.owned_id];
          if (!a || !b) return null;
          const x1 = a.x + NODEW, y1 = a.y + NODEH / 2;
          const x2 = b.x, y2 = b.y + NODEH / 2;
          const mx = (x1 + x2) / 2;
          const lit = isEdgeLit(e);
          const dim = hover && !lit;
          return (
            <g key={e.id} opacity={dim ? 0.18 : 1} style={{ transition: "opacity 150ms" }}>
              <path
                d={`M ${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2 - 8},${y2}`}
                fill="none"
                stroke={lit ? "var(--mk-primary)" : "rgba(176,141,87,0.4)"}
                strokeWidth={lit ? 2 : 1.25}
                markerEnd="url(#arrow)"
              />
              <g transform={`translate(${mx},${(y1 + y2) / 2})`}>
                <rect x={-17} y={-9} width={34} height={18} rx={9} fill="var(--mk-surface-2)" stroke="rgba(231,215,191,0.18)" />
                <text x={0} y={4} textAnchor="middle" className="font-data" fontSize="10" fill={lit ? "var(--mk-primary-2)" : "var(--mk-text-2)"}>{e.pct}%</text>
              </g>
            </g>
          );
        })}

        {/* nodes */}
        {nodes.map((n) => {
          const p = pos[n.id];
          if (!p) return null;
          const isMember = n.kind === "member";
          const lit = hover === n.id || isNodeLit(n.id);
          const dim = hover && !lit;
          const accent = isMember ? "#8AA6D6" : "#B08D57";
          return (
            <g
              key={n.id}
              transform={`translate(${p.x},${p.y})`}
              onMouseEnter={() => setHover(n.id)}
              onMouseLeave={() => setHover(null)}
              opacity={dim ? 0.35 : 1}
              style={{ cursor: "pointer", transition: "opacity 150ms" }}
              data-testid={`graph-node-${n.id}`}
            >
              <rect
                width={NODEW} height={NODEH} rx={12}
                fill="var(--mk-surface)"
                stroke={lit ? "var(--mk-primary)" : accent}
                strokeWidth={lit ? 2 : 1.25}
                style={{ filter: lit ? "drop-shadow(0 6px 16px rgba(0,0,0,0.5))" : "none" }}
              />
              <rect x={0} y={0} width={4} height={NODEH} rx={2} fill={accent} />
              <foreignObject x={12} y={8} width={NODEW - 20} height={NODEH - 16}>
                <div className="flex h-full items-center gap-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: accent + "22", color: accent }}>
                    {isMember ? <Users className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-medium leading-tight text-[color:var(--mk-text)]">{n.label}</div>
                    <div className="text-[9px] uppercase tracking-wider text-[color:var(--mk-muted)]">{n.kind}</div>
                  </div>
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
